/**
 * PublicQuiz — guest-facing prometric-style quiz page.
 * Route: /q/:token
 *
 * Flow: meta → sign-in (name+email +/- OTP) → intro → live (prometric)
 *      → result. No auth required.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Clock, ShieldAlert, Lock, ChevronLeft, ChevronRight, Send, Maximize, CheckCircle2, XCircle, Sun, Moon } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const STRICT_MAX = 3;

function fmtDur(s) { if (s < 0) s = 0; const m = Math.floor(s / 60); return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; }

export default function PublicQuiz() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | signin | otp | intro | live | submitting | done
  const [meta, setMeta] = useState(null);
  const [taker, setTaker] = useState({ name: "", email: "", otp: "" });
  const [capture, setCapture] = useState({});
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const [violations, setViolations] = useState(0);
  const [warning, setWarning] = useState(null);
  const [result, setResult] = useState(null);
  // R156 · Feb 2026 — Dark/light theme toggle persisted per browser.
  // R157c · Feb 2026 — Default to LIGHT (matches recruiter dashboard);
  // respect system preference when no explicit user choice is stored.
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem("acq.quiz.theme");
      if (stored === "light" || stored === "dark") return stored;
      // Respect OS-level preference on first load.
      if (typeof window !== "undefined" && window.matchMedia
          && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
      }
    } catch { /* noop */ }
    return "light";
  });
  const isDark = theme === "dark";
  useEffect(() => {
    try { localStorage.setItem("acq.quiz.theme", theme); } catch { /* noop */ }
  }, [theme]);
  // Palette helpers — swap between the two themes without a full rewrite.
  const T = isDark ? {
    root: "bg-neutral-950 text-neutral-100",
    card: "border-neutral-800 bg-neutral-950",
    header: "border-neutral-800 bg-neutral-900",
    input: "bg-neutral-900 border-neutral-700 text-neutral-100",
    muted: "text-neutral-500",
    dim: "text-neutral-400",
    option: "border-neutral-700 hover:border-neutral-500",
    optionSel: "border-emerald-400 bg-emerald-500/10",
    label: "text-neutral-100",
    btnOutline: "rounded-none border-neutral-700 text-neutral-100 hover:bg-neutral-900",
  } : {
    root: "bg-slate-50 text-slate-900",
    card: "border-slate-300 bg-white",
    header: "border-slate-300 bg-white",
    input: "bg-white border-slate-300 text-slate-900",
    muted: "text-slate-500",
    dim: "text-slate-600",
    option: "border-slate-300 hover:border-slate-500",
    optionSel: "border-emerald-500 bg-emerald-50",
    label: "text-slate-900",
    btnOutline: "rounded-none border-slate-300 text-slate-900 hover:bg-slate-100",
  };
  const sessionRef = useRef(null);
  const answersRef = useRef({}); answersRef.current = answers;

  const strict = session?.quiz?.prometric_mode === "strict";
  const enforce = strict || session?.quiz?.prometric_mode === "moderate";

  // ─── Load meta ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/quiz-links/${token}`);
        setMeta(data);
        if (data.invitee?.email) setTaker((t) => ({ ...t, email: data.invitee.email }));
        setPhase("signin");
      } catch (e) {
        toast.error(apiErrorMessage(e, "Invalid or expired link"));
        setPhase("error");
      }
    })();
  }, [token]);

  // ─── OTP request ──────────────────────────────────────────────
  const requestOtp = async () => {
    try {
      await axios.post(`${API}/public/quiz-links/${token}/otp-request`, { email: taker.email });
      toast.success("Code sent to your email");
      setPhase("otp");
    } catch (e) { toast.error(apiErrorMessage(e, "OTP request failed")); }
  };

  const verifyOtp = async () => {
    try {
      await axios.post(`${API}/public/quiz-links/${token}/otp-verify`, { email: taker.email, otp: taker.otp });
      setPhase("intro");
    } catch (e) { toast.error(apiErrorMessage(e, "Invalid code")); }
  };

  // ─── Start attempt ────────────────────────────────────────────
  const start = async () => {
    if (!taker.name.trim() || !taker.email.trim()) return toast.error("Name and email required");
    // Validate required hiring capture fields
    const hiringCfg = meta?.hiring?.capture_config || {};
    for (const [k, cfg] of Object.entries(hiringCfg)) {
      if (cfg?.enabled && cfg?.required) {
        const v = capture[k];
        if (v === undefined || v === null || String(v).trim() === "") {
          return toast.error(`${captureLabel(k)} is required`);
        }
      }
    }
    try {
      const payload = { name: taker.name, email: taker.email };
      if (meta?.hiring) payload.hiring_capture = capture;
      const { data } = await axios.post(`${API}/public/quiz-links/${token}/attempts`, payload);
      setSession(data);
      sessionRef.current = data;
      if (data.deadline_at) {
        const dl = new Date(data.deadline_at).getTime();
        setRemaining(Math.floor((dl - Date.now()) / 1000));
      }
      setPhase("live");
      if ((data.quiz.prometric_mode || "off") !== "off") {
        try { await document.documentElement.requestFullscreen({ navigationUI: "hide" }); } catch (_e) { /* user gesture */ }
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Start failed")); }
  };

  // ─── Timer ────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "live" || remaining === null) return;
    const t = setInterval(() => setRemaining((r) => {
      if (r === null) return null;
      if (r <= 1) { clearInterval(t); submitAttempt("timeout"); return 0; }
      return r - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [phase, remaining !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Submit ────────────────────────────────────────────────────
  const submitAttempt = useCallback(async (auto_reason = null) => {
    if (phase !== "live") return;
    setPhase("submitting");
    try {
      const { data } = await axios.post(
        `${API}/public/quiz-attempts/${sessionRef.current.attempt_id}/submit`,
        { session_token: sessionRef.current.session_token,
           answers: answersRef.current, auto_reason });
      try { if (document.fullscreenElement) await document.exitFullscreen(); } catch (_e) { /* noop */ }
      setResult(data);
      setPhase("done");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Submit failed"));
      setPhase("live");
    }
  }, [phase]);

  // ─── Violation logger ──────────────────────────────────────────
  const logViolation = useCallback(async (kind) => {
    if (!sessionRef.current || phase !== "live") return;
    try {
      const { data } = await axios.post(
        `${API}/public/quiz-attempts/${sessionRef.current.attempt_id}/violation`,
        { session_token: sessionRef.current.session_token, kind });
      const c = data.violation_count || 0;
      setViolations(c);
      setWarning({ kind, text: `${kindLabel(kind)} detected · violation ${c}${strict ? ` of ${STRICT_MAX}` : ""}` });
      setTimeout(() => setWarning(null), 4000);
      if (strict && c >= STRICT_MAX) {
        toast.error("Auto-submitted due to violations");
        submitAttempt("violations");
      }
    } catch (_e) { /* best-effort */ }
  }, [phase, strict, submitAttempt]);

  // ─── Enforcement listeners ─────────────────────────────────────
  useEffect(() => {
    if (phase !== "live" || !enforce) return;
    const onVis = () => document.hidden && logViolation("tab_switch");
    const onBlur = () => logViolation("blur");
    const onFs = () => !document.fullscreenElement && logViolation("fullscreen_exit");
    const onCtx = (e) => { e.preventDefault(); logViolation("context_menu"); };
    const onCopy = (e) => { e.preventDefault(); logViolation("copy"); };
    const onPaste = (e) => { e.preventDefault(); logViolation("paste"); };
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["c", "v", "x", "a", "p", "s"].includes(e.key.toLowerCase())) {
        e.preventDefault(); logViolation("copy", { key: e.key });
      }
      if (e.key === "F12" || (e.ctrlKey && e.shiftKey && ["i", "j", "c"].includes(e.key.toLowerCase()))) {
        e.preventDefault(); logViolation("devtools_suspected", { key: e.key });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("copy", onCopy);
    document.addEventListener("paste", onPaste);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("keydown", onKey);
    };
  }, [phase, enforce, logViolation]);

  const setAns = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));

  // ═══════════════════ Render ══════════════════════════════════
  if (phase === "loading") return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (phase === "error") return <div className="min-h-screen flex items-center justify-center text-red-600">Invalid or expired link.</div>;

  if (phase === "signin" || phase === "otp") {
    return (
      <div className={`min-h-screen ${T.root} flex items-center justify-center p-6`}>
        <Toaster theme={isDark ? "dark" : "light"} />
        <div className={`max-w-md w-full border p-6 space-y-4 ${T.card}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {meta?.workspace_logo && (
                <img src={meta.workspace_logo} alt="" className="h-8 max-w-[160px] object-contain mb-2" data-testid="quiz-workspace-logo" />
              )}
              <div className={`text-[10px] uppercase tracking-widest ${T.muted}`}>{meta?.workspace_name}</div>
              <h1 className="text-xl font-semibold">{meta?.quiz?.title}</h1>
              {meta?.quiz?.description && <p className={`text-sm mt-1 ${T.dim}`}>{meta.quiz.description}</p>}
            </div>
            <ThemeToggle isDark={isDark} onToggle={() => setTheme(isDark ? "light" : "dark")} />
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div className="border border-neutral-800 p-2">
              <div className="label-eyebrow text-neutral-500">Questions</div>
              <div className="text-lg font-semibold">{meta?.quiz?.question_count}</div>
            </div>
            <div className="border border-neutral-800 p-2">
              <div className="label-eyebrow text-neutral-500">Time limit</div>
              <div className="text-lg font-semibold">{meta?.quiz?.time_limit_min ? `${meta.quiz.time_limit_min} min` : "—"}</div>
            </div>
          </div>

          {phase === "signin" && (
            <>
              <div>
                <Label className="text-xs mb-1 block">Full name</Label>
                <Input value={taker.name} onChange={(e) => setTaker({ ...taker, name: e.target.value })} className={`h-9 rounded-none ${T.input}`} data-testid="pquiz-name" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Email</Label>
                <Input type="email" value={taker.email} onChange={(e) => setTaker({ ...taker, email: e.target.value })} disabled={!!meta?.invitee?.email} className={`h-9 rounded-none ${T.input}`} data-testid="pquiz-email" />
              </div>
              {meta?.hiring && (
                <div className="space-y-3 border-t border-neutral-800 pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Candidate profile for {meta.hiring.role_title}</div>
                  {Object.entries(meta.hiring.capture_config || {}).filter(([_k, cfg]) => cfg?.enabled).map(([k, cfg]) => (
                    <div key={k}>
                      <Label className="text-xs mb-1 block">
                        {captureLabel(k)}{cfg.required && <span className="text-rose-400 ml-1">*</span>}
                      </Label>
                      <Input
                        type={captureInputType(k)}
                        value={capture[k] || ""}
                        onChange={(e) => setCapture({ ...capture, [k]: e.target.value })}
                        placeholder={capturePlaceholder(k)}
                        className={`h-9 rounded-none ${T.input}`}
                        data-testid={`pquiz-capture-${k}`}
                      />
                    </div>
                  ))}
                </div>
              )}
              {meta?.link?.require_otp ? (
                <Button className="w-full rounded-none bg-emerald-600 hover:bg-emerald-500" onClick={requestOtp} disabled={!taker.email || !taker.name} data-testid="pquiz-req-otp">
                  Send access code
                </Button>
              ) : (
                <Button className="w-full rounded-none bg-emerald-600 hover:bg-emerald-500" onClick={() => setPhase("intro")} disabled={!taker.email || !taker.name}>
                  Continue
                </Button>
              )}
            </>
          )}
          {phase === "otp" && (
            <>
              <div className="text-xs text-neutral-400">We sent a 6-digit code to <b>{taker.email}</b>. Enter it below.</div>
              <Input value={taker.otp} onChange={(e) => setTaker({ ...taker, otp: e.target.value })} placeholder="123456" className={`h-10 rounded-none ${T.input} text-center text-xl font-mono`} data-testid="pquiz-otp" />
              <Button className="w-full rounded-none bg-emerald-600 hover:bg-emerald-500" onClick={verifyOtp} disabled={!taker.otp}>
                Verify code
              </Button>
              <button className="text-xs text-neutral-400 underline w-full" onClick={requestOtp}>Resend code</button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (phase === "intro") {
    return (
      <div className={`min-h-screen ${T.root} flex items-center justify-center p-6`}>
        <div className="max-w-lg w-full border border-neutral-800 p-6 space-y-4">
          <div className="flex items-center gap-2 text-amber-400">
            <ShieldAlert className="w-5 h-5" />
            <h1 className="text-xl font-semibold">Secure Assessment</h1>
          </div>
          <p className="text-sm text-neutral-300">You&apos;re about to begin a proctored assessment. On start, your browser will enter fullscreen and monitoring will activate.</p>
          <ul className="text-sm text-neutral-400 space-y-1 list-disc pl-4">
            <li>Do not switch tabs, minimize, or exit fullscreen.</li>
            <li>Copy, paste, right-click and devtools shortcuts are blocked.</li>
            <li>Strict mode: 3 violations will auto-submit your attempt.</li>
            <li>Timer, if enabled, continues even if you close the tab.</li>
          </ul>
          <Button className="w-full rounded-none bg-emerald-600 hover:bg-emerald-500" onClick={start} data-testid="pquiz-start">
            <Maximize className="w-4 h-4 mr-1" /> I understand — start
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "submitting") return <div className={`min-h-screen ${T.root} flex items-center justify-center`}>Submitting…</div>;

  if (phase === "done" && result) {
    return (
      <div className={`min-h-screen ${T.root} p-6`}>
        <div className="max-w-2xl mx-auto space-y-4">
          <div className={`border-2 p-6 text-center ${result.pass ? "border-emerald-500 bg-emerald-500/10" : "border-rose-500 bg-rose-500/10"}`}>
            {result.pass ? <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-2" /> : <XCircle className="w-12 h-12 text-rose-400 mx-auto mb-2" />}
            <h1 className="text-2xl font-semibold">{result.pass ? "You passed!" : "Not this time"}</h1>
            <div className="text-4xl font-bold mt-2 font-mono">{result.score_percent}%</div>
            <div className="text-sm text-neutral-400 mt-1">Threshold: {result.pass_percent}%</div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Live quiz ────────────────────────────────────────────────
  if (!session) return null;
  const q = session.questions[idx];
  const answered = Object.keys(answers).filter((k) => {
    const v = answers[k];
    return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  }).length;

  return (
    <div className={`min-h-screen ${T.root} select-none`}>
      <Toaster theme={isDark ? "dark" : "light"} />
      <div className={`sticky top-0 border-b ${isDark ? "border-neutral-800 bg-neutral-900/95" : "border-slate-200 bg-white/95"} backdrop-blur px-4 py-2 flex items-center gap-3`}>
        <ShieldAlert className="w-4 h-4 text-amber-500" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{session.quiz.title}</div>
          <div className={`text-[10px] ${T.muted}`}>
            {strict ? "Strict proctoring" : session.quiz.prometric_mode === "moderate" ? "Moderate proctoring" : "Open"}
          </div>
        </div>
        {remaining !== null && (
          <div className={`flex items-center gap-1 font-mono text-lg ${remaining < 60 ? (isDark ? "text-red-400" : "text-red-600") : ""}`}>
            <Clock className="w-4 h-4" /> {fmtDur(remaining)}
          </div>
        )}
        {enforce && <div className="flex items-center gap-1 text-xs"><Lock className="w-3 h-3" />{violations}{strict ? `/${STRICT_MAX}` : ""}</div>}
        <ThemeToggle isDark={isDark} onToggle={() => setTheme(isDark ? "light" : "dark")} />
      </div>

      {warning && (
        <div className="fixed top-14 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-4 py-2 shadow-lg flex items-center gap-2 animate-pulse">
          <AlertTriangle className="w-4 h-4" /> {warning.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr]">
        <aside className={`border-r ${isDark ? "border-neutral-800 bg-neutral-900/40" : "border-slate-200 bg-slate-50"} p-2 overflow-auto max-h-[calc(100vh-52px)]`}>
          <div className={`text-[10px] uppercase tracking-widest ${T.muted} mb-2`}>{answered}/{session.questions.length} answered</div>
          <div className="grid grid-cols-5 md:grid-cols-4 gap-1">
            {session.questions.map((qq, i) => {
              const v = answers[qq.id];
              const isAns = v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
              return (
                <button key={qq.id} onClick={() => setIdx(i)} className={`h-8 text-xs font-mono border ${idx === i ? "border-emerald-400 bg-emerald-500/20" : isAns ? (isDark ? "border-neutral-700 bg-neutral-800" : "border-slate-300 bg-slate-200") : (isDark ? "border-neutral-700" : "border-slate-300")}`}>{i + 1}</button>
              );
            })}
          </div>
          <Button className="w-full mt-3 rounded-none bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => submitAttempt("manual")} data-testid="pquiz-submit">
            <Send className="w-3.5 h-3.5 mr-1" /> Submit
          </Button>
        </aside>
        <main className="p-6 max-w-3xl mx-auto w-full">
          <div className={`text-[10px] uppercase tracking-widest ${T.muted} mb-1`}>Question {idx + 1} of {session.questions.length} · {q.points} pt{q.points === 1 ? "" : "s"}</div>
          <h2 className="text-xl font-semibold mb-2">{q.text}</h2>
          {q.description && <p className="text-sm text-neutral-400 mb-3">{q.description}</p>}
          <PublicQInput q={q} value={answers[q.id]} onChange={(v) => setAns(q.id, v)} T={T} />
          <div className="mt-6 flex items-center gap-2">
            <Button variant="outline" className={T.btnOutline} onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <Button variant="outline" className={T.btnOutline} onClick={() => setIdx((i) => Math.min(session.questions.length - 1, i + 1))} disabled={idx === session.questions.length - 1}>
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </main>
      </div>
    </div>
  );
}

function kindLabel(k) {
  return ({ tab_switch: "Tab switch", blur: "Focus lost", fullscreen_exit: "Fullscreen exit",
              context_menu: "Right-click blocked", copy: "Copy blocked", paste: "Paste blocked",
              devtools_suspected: "Devtools shortcut blocked" })[k] || "Violation";
}

function captureLabel(k) {
  return ({ phone: "Phone number", linkedin_url: "LinkedIn URL",
    resume_url: "Resume URL", current_ctc: "Current CTC (annual)",
    notice_period: "Notice period (days)",
    years_experience: "Years of experience" })[k] || k;
}
function captureInputType(k) {
  if (k === "linkedin_url" || k === "resume_url") return "url";
  if (k === "phone") return "tel";
  if (k === "years_experience" || k === "notice_period") return "number";
  return "text";
}
function capturePlaceholder(k) {
  return ({ phone: "+91 98765 43210",
    linkedin_url: "https://linkedin.com/in/…",
    resume_url: "https://drive.google.com/…",
    current_ctc: "e.g. 12,00,000",
    notice_period: "e.g. 30",
    years_experience: "e.g. 4" })[k] || "";
}

function PublicQInput({ q, value, onChange, T }) {
  if (q.type === "mcq" || q.type === "truefalse") {
    return (
      <div className="space-y-2">
        {q.options.map((o) => (
          <label key={o.key} className={`block border p-3 cursor-pointer ${value === o.key ? T.optionSel : T.option} ${T.label}`}>
            <input type="radio" name={q.id} className="mr-2" checked={value === o.key} onChange={() => onChange(o.key)} />
            <span className={`font-mono text-xs mr-1 ${T.muted}`}>{o.key}.</span>{o.label}
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "multi") {
    const v = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        {q.options.map((o) => (
          <label key={o.key} className={`block border p-3 cursor-pointer ${v.includes(o.key) ? T.optionSel : T.option} ${T.label}`}>
            <input type="checkbox" className="mr-2" checked={v.includes(o.key)} onChange={(e) => onChange(e.target.checked ? [...v, o.key] : v.filter((x) => x !== o.key))} />
            <span className={`font-mono text-xs mr-1 ${T.muted}`}>{o.key}.</span>{o.label}
          </label>
        ))}
      </div>
    );
  }
  return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} className={`rounded-none ${T.input}`} />;
}

// R156 · Feb 2026 — Dark/light toggle for the public quiz. Kept purely
// visual — no shadcn dark-class trick, just swaps our own palette map
// via the `T` object built at the top of PublicQuiz.
function ThemeToggle({ isDark, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label="Toggle theme"
      title={isDark ? "Switch to light" : "Switch to dark"}
      className={`h-8 w-8 border rounded-none inline-flex items-center justify-center shrink-0 ${isDark ? "border-neutral-700 hover:bg-neutral-900" : "border-slate-300 hover:bg-slate-100"}`}
      data-testid="quiz-theme-toggle"
    >
      {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}

